import { Injectable, Injector, computed, effect, inject, signal } from '@angular/core';

import { delayFor } from 'app/core/utilities/async-utils';
import { Loggable } from 'app/utils/logging/loggable';
import { OutgoingMessageType, TeleportMessage } from 'messages/websocket.messages';

import { Player } from '../social-pixi-canvas/player';

import { WebsocketService } from './websocket.service';
import { RoomPresenceService } from './room/room-presence.service';
import { MatDialog } from '@angular/material/dialog';
import {
  ModalSimpleSocialComponent,
  ModalSimpleSocialData,
} from '../modal-simple-social/modal-simple-social.component';
import { socialAppActions, socialAppSelectors } from '../state/app';
import { Store } from '@ngrx/store';
import { SceneService } from './scene.service';

interface TeleportChangeRoomData {
  mainPlayer?: Player;
  toTeleportPlayer?: Player;
  userId: string;
  ringColor: string;
}

interface TeleportFlowData {
  userId: string;
  ringColor: string;
  mainPlayer?: Player;
  toTeleportPlayer?: Player;
  isCrossRoomTeleport?: boolean;
  toTeleportRoomId?: string;
  teleportingFromChangeRoomEvent?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class TeleportService extends Loggable {
  private websocketService = inject(WebsocketService);
  private sceneService = inject(SceneService);
  private injector = inject(Injector);
  private store = inject(Store);
  private dialog = inject(MatDialog);

  private _roomPresenceService?: RoomPresenceService;

  private readonly _teleportEngaged = signal(false);
  public teleportEngaged = this._teleportEngaged.asReadonly();

  private readonly _dispatchedChangeRoom = signal(false);
  private readonly _teleportChangeRoomData = signal<TeleportChangeRoomData | undefined>(undefined);

  private readonly currentSocial = computed(() => this.store.selectSignal(socialAppSelectors.getCurrentSocial)());

  public constructor() {
    super();
    this.setDisplayName('TeleportService');

    effect(async () => {
      const social = this.currentSocial();

      if (social && this._dispatchedChangeRoom()) {
        await delayFor(2000);
        this._dispatchedChangeRoom.set(false);

        const teleportData = this._teleportChangeRoomData();
        const updatedPlayerData = teleportData ? this.sceneService.getPlayer(teleportData.userId) : undefined;

        if (teleportData && updatedPlayerData) {
          try {
            await this.teleportFlow({
              userId: updatedPlayerData.userId,
              ringColor: updatedPlayerData.ringColor || teleportData.ringColor,
              mainPlayer: teleportData.mainPlayer,
              toTeleportPlayer: updatedPlayerData,
              teleportingFromChangeRoomEvent: true,
            });
          } catch (e) {
            this.error('Failed to teleport user to other participant', e);
          } finally {
            this._teleportEngaged.set(false);
          }
        } else {
          this.error('Failed to teleport user to other participant, player not found');
        }
      }
    });
  }

  private get roomPresenceService(): RoomPresenceService {
    if (!this._roomPresenceService) {
      this._roomPresenceService = this.injector.get(RoomPresenceService);
    }
    return this._roomPresenceService;
  }

  public closeBadge(): void {
    this.store.dispatch(socialAppActions.TOGGLE_BADGE_OPEN({ open: false, userId: '', ringColor: '' }));
  }

  public async teleportFlow(teleportFlowData: TeleportFlowData): Promise<void> {
    const {
      isCrossRoomTeleport,
      mainPlayer,
      ringColor,
      teleportingFromChangeRoomEvent,
      toTeleportPlayer,
      toTeleportRoomId,
      userId,
    } = teleportFlowData;

    // Check if the user is already in the room
    if (!isCrossRoomTeleport) {
      if (!teleportingFromChangeRoomEvent) {
        await this.fadeOutAndEngageTeleport(mainPlayer);
      }
      if (toTeleportPlayer) {
        this.fadeInAndTeleport(toTeleportPlayer, userId, ringColor, mainPlayer);
      } else {
        this.error('Failed to teleport user to other participant, player not found');
        return;
      }
    } else {
      // Cross room teleport
      if (toTeleportRoomId) {
        this._teleportChangeRoomData.set({
          mainPlayer: mainPlayer,
          toTeleportPlayer: toTeleportPlayer,
          userId: userId,
          ringColor: ringColor,
        });
        this.handleChangeRoomToTeleport(toTeleportRoomId, mainPlayer, userId, ringColor);
      } else {
        this.error('Failed to teleport user to other room, room id not found');
        return;
      }
    }
  }

  public async fadeOutAndEngageTeleport(mainPlayer?: Player): Promise<void> {
    // Fade the player out and engage teleport
    await delayFor(100);
    this._teleportEngaged.set(true);
    await delayFor(1500);
    mainPlayer?.fadeOut(1500, 0);
    this.closeBadge();
    await delayFor(1500);
    this.store.dispatch(socialAppActions.TOGGLE_SEARCH_PARTICIPANTS({ open: false }));
  }

  public fadeInAndTeleport(toTeleportPlayer: Player, userId: string, ringColor: string, mainPlayer?: Player): void {
    // Fade the player in and execute teleport
    try {
      this.onTeleport(toTeleportPlayer.x.toString(), (toTeleportPlayer.y + 400).toString());
      mainPlayer?.fadeIn(1500);
    } catch (e) {
      mainPlayer?.fadeIn(1500);
      this.store.dispatch(
        socialAppActions.TOGGLE_BADGE_OPEN({ open: true, userId: `${userId}`, ringColor: ringColor }),
      );
      this.error('Failed to teleport user to other participant', e);
    }
    this._teleportEngaged.set(false);
  }

  public onTeleport(teleportX?: string, teleportY?: string): void {
    const msg: TeleportMessage = {
      t: OutgoingMessageType.TELEPORT,
      x: Number(teleportX),
      y: Number(teleportY),
    };

    this.info('Teleporting to', msg);
    this.websocketService.sendMessage(msg);
  }

  public handleChangeRoomToTeleport(socialId: string, mainPlayer?: Player, userId?: string, ringColor?: string): void {
    // Change room to teleport user
    const data = {
      modalTitle: 'Change Room',
      text: 'Are you sure you want to leave this room?',
      primaryButtonText: 'Continue',
      tertiaryButtonText: 'Cancel',
    };

    const dialogRef = this.dialog.open<ModalSimpleSocialComponent, ModalSimpleSocialData>(ModalSimpleSocialComponent, {
      data,
    });

    dialogRef.afterClosed().subscribe((confirm: 'primary' | 'tertiary' | undefined) => {
      if (confirm === 'primary') {
        this._dispatchedChangeRoom.set(true);
        this.fadeOutAndEngageTeleport(mainPlayer)
          .then(() => {
            if (this.roomPresenceService) {
              this.roomPresenceService.changeRoom(socialId);
            } else {
              this.error('Room presence service not found');
              this._dispatchedChangeRoom.set(false);
              mainPlayer?.fadeIn(1500);
              this.store.dispatch(
                socialAppActions.TOGGLE_BADGE_OPEN({ open: true, userId: `${userId}`, ringColor: ringColor }),
              );
            }
          })
          .catch((e) => {
            this.error('Failed to change room: ', e);
            this._dispatchedChangeRoom.set(false);

            mainPlayer?.fadeIn(1500);
            this.store.dispatch(
              socialAppActions.TOGGLE_BADGE_OPEN({ open: true, userId: `${userId}`, ringColor: ringColor }),
            );
          });
        return;
      } else {
        this._dispatchedChangeRoom.set(false);
        this._teleportChangeRoomData.set(undefined);
      }
    });
  }

  public async teleport(mainPlayer: Player, x: number, y: number): Promise<void> {
    this._teleportEngaged.set(true);
    await delayFor(1500);
    if (mainPlayer) {
      mainPlayer.fadeOut(1500, 0);
      await delayFor(1500);
      try {
        mainPlayer.fadeIn(1500);
        this.onTeleport(x.toString(), y.toString());
        this._teleportEngaged.set(false);
      } catch (e) {
        mainPlayer.fadeIn(1500);
        this._teleportEngaged.set(false);
        this.error('Failed to teleport user to other participant', e);
      }
    }
  }
}
